// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import { Box, Chip, Grid, Tooltip, Typography } from '@mui/material';
import { dateTime } from '../../utils/dates';
import { DETECTOR_STATES } from '../../utils/detecteur';
import { UpdateFailure, StepIcon } from './commun';

// Mêmes libellés que la fiche de l'établissement, sauf l'absence de rapport :
// sur un serveur qu'on inspecte, elle se lit « état inconnu ».
const SERVER_STATES = {
  ...DETECTOR_STATES,
  unknown: { label: 'État inconnu', couleur: 'default' },
};

function Fact({ titre, children }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary">{titre}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>{children}</Box>
    </Grid>
  );
}

/**
 * Détail d'un serveur d'établissement. Il ne rapporte pas les étapes
 * d'installation d'une box de foyer, mais l'étape `pose_cle` (POST
 * /api/provision/state, par la clé de pose) et sa santé toutes les cinq minutes
 * (lot de POST /api/site-state/report), que `GET /api/kits` rend dans
 * `detector`.
 */
export default function SiteServerDetail({ serveur: server, steps, details }) {
  const d = server.detector || {};
  const state = SERVER_STATES[d.detector_state] || SERVER_STATES.unknown;
  const clock = d.link?.clock || {};
  const update = server.mise_a_jour || {};
  const updateParts = [
    update.socle && `socle ${update.socle}`,
    update.modeles && `modèles ${update.modeles}`,
    update.image && `image ${update.image}`,
  ].filter(Boolean);
  const installation = steps.pose_cle;
  const installationLabel = installation === 'ok' ? 'Posé' : installation === 'error' ? 'En échec' : installation === 'pending' ? 'En cours' : 'Pas encore posé';
  return (
    <Grid container spacing={1.5}>
      <Fact titre="Pose par clé">
        <StepIcon status={installation || 'pending'} />
        <Typography variant="body2" color={installation === 'error' ? 'error' : 'text.primary'}>{installationLabel}</Typography>
        {details.pose_cle && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ width: '100%' }}>
            {details.pose_cle}
          </Typography>
        )}
      </Fact>
      <Fact titre="Détection">
        <Chip size="small" label={state.label} color={state.couleur} />
        {d.detection_active === null && d.reported_at && (
          <Typography variant="caption" color="text.secondary">ni active ni en panne déclarée</Typography>
        )}
        {d.detector_reason && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ width: '100%' }}>
            {d.detector_reason}
          </Typography>
        )}
      </Fact>
      <Fact titre="Dernier rapport de santé">
        <Typography variant="body2">{d.reported_at ? dateTime(d.reported_at, '—') : 'aucun'}</Typography>
      </Fact>
      <Fact titre="Caméras vues par le serveur">
        <Typography variant="body2">
          {(d.cameras_reachable ?? 0)} joignable(s) sur {(d.cameras_configured ?? 0)} configurée(s)
        </Typography>
      </Fact>
      <Fact titre="Horloge">
        {clock.timestamps_reliable === true && <Chip size="small" color="success" label="Fiable" />}
        {clock.timestamps_reliable === false && <Chip size="small" color="error" label="Dérive" />}
        {clock.timestamps_reliable == null && <Chip size="small" label="Non établie" />}
        {typeof clock.skew_s === 'number' && (
          <Typography variant="caption" color="text.secondary">écart {clock.skew_s.toFixed(1)} s</Typography>
        )}
      </Fact>
      <Fact titre="Dernière inférence">
        <Typography variant="body2">{d.last_inference_at ? dateTime(d.last_inference_at, '—') : 'aucune'}</Typography>
      </Fact>
      <Fact titre="Mise à jour">
        {!update.rapporte ? (
          <Typography variant="body2" color="text.secondary">
            aucun état reçu (serveur posé avant le canal, ou jamais mis à jour)
          </Typography>
        ) : update.echec ? (
          <>
            <UpdateFailure echec={update.echec} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ width: '100%' }}>
              {update.echec.version ? `version ${update.echec.version} : ` : ''}{update.echec.motif || 'sans motif'}
              {update.echec.le ? ` (${dateTime(update.echec.le, '—')})` : ''}
            </Typography>
          </>
        ) : (
          <Typography variant="body2">
            {update.applique_le ? `appliquée le ${dateTime(update.applique_le, '—')}` : 'aucune application par le canal'}
          </Typography>
        )}
        {/* Le backend garde le dernier état reçu quand un rapport part sans
            résumé (maj.json illisible un passage) : sa date dit s'il est frais. */}
        {update.rapporte && update.recu_le && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ width: '100%' }}>
            état reçu le {dateTime(update.recu_le, '—')}
          </Typography>
        )}
      </Fact>
      {updateParts.length > 0 && (
        <Fact titre="Pièces en service">
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
            {updateParts.join(', ')}
          </Typography>
        </Fact>
      )}
      {(update.refusees || []).length > 0 && (
        <Fact titre="Versions refusées sur ce serveur">
          {update.refusees.map((r, i) => (
            <Tooltip key={`${r.version}-${i}`} title={r.motif || ''}>
              <Chip size="small" variant="outlined" label={r.version || 'version inconnue'} />
            </Tooltip>
          ))}
        </Fact>
      )}
      {(update.manques || []).length > 0 && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Manques à la dernière provision</Typography>
          {update.manques.map((m, i) => (
            <Typography key={i} variant="body2" color="warning.main" sx={{ fontSize: '0.8rem' }}>{m}</Typography>
          ))}
        </Grid>
      )}
    </Grid>
  );
}
